/**
 * Bomb panel: planted fuse, "you have the bomb" marker, plant/defuse progress.
 *
 * The planted-fuse pulse is integrated from the wall clock in update() rather
 * than a CSS keyframe, so it freezes with the engine when the loop is pumped.
 */
import { el, setClock, setPct, setClass, setText, setOpacity, clamp01 } from './dom.js';

/** Fuse length used purely to draw the countdown bar; CS:GO is 40 s. */
const FUSE_FULL = 40;

export class BombPanel {
  constructor(parent) {
    const root = el('div', 'cs-bombwrap', parent);
    this.root = root;

    const fuse = el('div', 'cs-fuse', root);
    const top = el('div', 'cs-fuse-top', fuse);
    el('i', 'cs-fuse-ico', top, 'C4');
    this.fuseTime = el('span', 'cs-fuse-time', top, '0:00');
    const fbar = el('div', 'cs-fuse-bar', fuse);
    this.fuseFill = el('i', null, fbar);
    this.fuse = fuse;

    this.carry = el('div', 'cs-carry', root, 'YOU HAVE THE BOMB');

    const prog = el('div', 'cs-prog', root);
    this.progLabel = el('span', 'cs-prog-label', prog, 'PLANTING');
    const pbar = el('div', 'cs-prog-bar', prog);
    this.progFill = el('i', null, pbar);
    this.prog = prog;

    this._mode = 0; // 0 none, 1 planting, 2 defusing
  }

  update(s, now) {
    const bomb = s?.bomb ?? null;
    const planted = !!bomb && bomb.state === 'planted';

    setClass(this.fuse, 'on', planted);
    if (planted) {
      const fuse = bomb.fuse ?? 0;
      setClock(this.fuseTime, fuse);
      setPct(this.fuseFill, 'width', fuse / FUSE_FULL);
      const danger = fuse < 10;
      setClass(this.fuse, 'danger', danger);
      // 4 Hz breathing, 0.72..1.0 — driven from `now` so it is deterministic.
      const beat = danger ? 8 : 4;
      const amp = danger ? 0.28 : 0.12;
      setOpacity(this.fuse, 1 - amp * (0.5 + 0.5 * Math.sin(now * beat)));
    }

    setClass(this.carry, 'on', !!s?.hasBomb);

    const pp = s?.plantProgress ?? 0;
    const dp = s?.defuseProgress ?? 0;
    const mode = pp > 0 ? 1 : dp > 0 ? 2 : 0;
    const on = mode !== 0;
    setClass(this.prog, 'on', on);
    if (on) {
      if (mode !== this._mode) {
        this._mode = mode;
        setText(this.progLabel, mode === 1 ? 'PLANTING' : 'DEFUSING');
        setClass(this.prog, 'defuse', mode === 2);
      }
      setPct(this.progFill, 'width', clamp01(mode === 1 ? pp : dp));
    } else {
      this._mode = 0;
    }
  }
}
